import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ReportEntity,
  WorkDayProduct,
  useCreateIncidentWork,
  useIncidentWork,
  useRemoveIncidentWork,
  useUpdateIncidentWork,
} from '@data';
import { useStore } from '@data/storages';
import { useOrganizationId } from '@app/utils/hooks';
import { clockTimeOf } from '@app/components/shared/phillReport/incidentLength';
import ReportIncidentDialog from '@app/components/shared/phillReport/ReportIncidentDialog';
import RemoveReportModal from '@app/components/shared/removeReportModal';
import DayLog from '../sprintProduct/DayLog';
import { IncidentEntry, IncidentWorkDay, groupEntries, incidentEntryFromApi } from './sources/incidents';

/** Who booked time against one incident, over the sprint report's own `DayLog` rather than a copy of it. */

// The membership's user, not the row's id: that is where the edit form reads whose day it writes back to.
const asReport = (entry: IncidentEntry, incidentId?: string): ReportEntity =>
  ({
    id: entry.id,
    today: entry.today,
    workday: entry.workday,
    minutes: Math.round(entry.hours * 60),
    startedAt: entry.startedAt ?? undefined,
    // Every row here is incident work by definition, which is what sends Edit to the incident form.
    incidentId,
    involvement: { membership: { id: entry.id, user: { id: entry.userId, firstName: entry.name } } },
  }) as unknown as ReportEntity;

/** The stretch of clock an entry covers. Nothing for one booked before the form asked for a start. */
const spanOf = (entry: IncidentEntry): string | null => {
  if (!entry.startedAt) return null;
  const from = new Date(entry.startedAt);

  // Back to minutes first: 0.83 stored hours would draw an end a minute early.
  return `${clockTimeOf(from)} – ${clockTimeOf(new Date(from.getTime() + Math.round(entry.hours * 60) * 60000))}`;
};

const asDays = (days: IncidentWorkDay[], incidentId?: string): WorkDayProduct[] =>
  days.map(day => ({
    date: day.date,
    totalMinutes: Math.round(day.hours * 60),
    // No chapter list is passed to DayLog, so it falls back to this name — which is already the label.
    chapters: day.chapters.map(chapter => ({
      name: chapter.label,
      totalMinutes: Math.round(chapter.hours * 60),
      reports: chapter.entries.map(entry => asReport(entry, incidentId)),
    })),
  })) as WorkDayProduct[];

// Support hours are not priced yet: null everywhere means DayLog shows hours and no euros.

const IncidentWorkLog = ({
  productId,
  incidentId,
  expandAll = false,
  canEdit,
  canAdd = true,
  addForDay,
  onAddForDayHandled,
}: {
  productId?: string;
  incidentId?: string;
  expandAll?: boolean;
  /** A client reads this log and never writes to it — the same answer the page's other actions give. */
  canEdit?: boolean;
  /** Whether more time may be booked. Off once the record is closed, which is what closing means. */
  canAdd?: boolean;
  /** A day the page above asked to book on, so its own button opens the form the day rows open. */
  addForDay?: string;
  onAddForDayHandled?: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  // Booking is done as yourself: an hour added here is your own day's entry, the same as from the header.
  const { tokenData } = useStore();
  const organizationId = useOrganizationId();
  const { data, isLoading } = useIncidentWork(productId, incidentId);
  const createWork = useCreateIncidentWork(tokenData?.userId ?? '', organizationId ?? '');
  const updateWork = useUpdateIncidentWork();
  const removeWork = useRemoveIncidentWork();

  const [dayToAdd, setDayToAdd] = useState<string>();

  // Set by the page's own button, which may press before this tab has mounted, so it arrives as a prop.
  useEffect(() => {
    if (addForDay) setDayToAdd(addForDay);
  }, [addForDay]);
  const [reportToEdit, setReportToEdit] = useState<ReportEntity>();
  const [reportToRemove, setReportToRemove] = useState<ReportEntity>();

  const entries: IncidentEntry[] = useMemo(() => (data?.items ?? []).map(incidentEntryFromApi), [data?.items]);

  const days = useMemo(() => asDays(groupEntries(entries), incidentId), [entries, incidentId]);

  // By the entry's own id, which `asReport` carries through as the report's: the way back to the entry.
  const byId = useMemo(() => new Map(entries.map(entry => [entry.id, entry])), [entries]);
  const timeFor = (report: ReportEntity): ReactElement | null => {
    const entry = byId.get(report.id);
    const span = entry && spanOf(entry);
    if (!span) return null;

    return (
      <span className="flex items-center gap-2">
        {/* The same words the client's own document prints on these rows, so the two read as one fact. */}
        {entry?.outOfHours && (
          <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {t('incidents.outOfHours')}
          </span>
        )}
        <span className="text-xs">{span}</span>
      </span>
    );
  };

  if (isLoading) {
    return <p className="py-6 text-center text-sm text-muted-foreground">{t('incidents.workLoading')}</p>;
  }

  // These are real time entries, so an hour fixed here is fixed in the person's day too.
  const authoring = canEdit
    ? {
        ...(canAdd ? { onAddForDay: setDayToAdd } : {}),
        onEditReport: setReportToEdit,
        onRemoveReport: setReportToRemove,
      }
    : undefined;

  return (
    <>
      {/* No button on the empty line: the day rows carry the plus, and the page's own is in its header. */}
      {days.length ? (
        <DayLog days={days} expandAll={expandAll} timeFor={timeFor} authoring={authoring} />
      ) : (
        <p className="py-6 text-center text-sm text-muted-foreground">{t('incidents.noWorkReportedYet')}</p>
      )}

      {/* Both the plus and the pencil open the incident form, because every row here is incident work:
          a day and two clock times, not a number of hours. The incident is fixed — this log is one
          incident's — so the form opens on it rather than asking which. */}
      <ReportIncidentDialog
        show={!!dayToAdd}
        onHide={() => {
          setDayToAdd(undefined);
          onAddForDayHandled?.();
        }}
        userId={tokenData?.userId}
        against={{ productId: productId ?? '', incidentId: incidentId ?? '' }}
        prefill={dayToAdd ? { workday: new Date(dayToAdd) } : null}
        onSubmit={createWork}
      />

      <ReportIncidentDialog
        show={!!reportToEdit}
        onHide={() => setReportToEdit(undefined)}
        userId={reportToEdit?.involvement?.membership?.user?.id}
        report={reportToEdit ?? null}
        // The entries come back without their product, and this log is that product's.
        against={{ productId: productId ?? '', incidentId: incidentId ?? '' }}
        onSubmit={values => updateWork.mutateAsync(values)}
      />

      <RemoveReportModal
        show={!!reportToRemove}
        onHide={() => setReportToRemove(undefined)}
        onRemove={() => {
          if (reportToRemove) removeWork.mutate(reportToRemove.id);
          setReportToRemove(undefined);
        }}
      />
    </>
  );
};

export default IncidentWorkLog;
